/* eslint-disable import/no-extraneous-dependencies */
import { EVENT_LABEL } from "constants/index";
import { SUPPORTED_LANG } from "constants/configConstants";
import { sendWebViewEvent } from "@Utils/webviewEvents";
import { WEB_VIEW_APP_EVENTS, APP_EVENTS } from "@Utils/webApputils/analyticEvents";
import { logAppEvent } from "@Utils/appAnalytics";
import config from "@Config/index";
import { saveWhitelabelLanguageChoice } from "@Utils/whitelabelLocalization";
import setCookie from "helpers/setCookie";
import getCookieDomain from "helpers/getCookieDomain";
import useIsLanguage from "./useIsLanguage";
import useIsApp from "./useIsApp";
import {
  saveLanguagePreference,
  buildLanguageUrl,
  customDomainSwitch,
  extractLanguageFromPath,
  isChinaSameDomainLanguage,
} from "@Utils/languageCore";

const { LANGUAGE_SELECTED } = EVENT_LABEL;

const useLanguageSelector = ({ handleEventEmitter }: any) => {
  const userLanguage = useIsLanguage();
  const isApp = useIsApp();
  const { LANGUAGE_MODAL_CLICKED } = EVENT_LABEL;

  const onCLickLanguageSelector = () => {
    logAppEvent({
      eventName: APP_EVENTS.CTA_CLICKED,
      payload: {
        cta_name: "language_selector_button",
        source: "homepage",
        current_language: userLanguage,
        current_path: typeof window !== "undefined" ? window.location.pathname : "",
      },
    });

    handleEventEmitter({ action: LANGUAGE_MODAL_CLICKED });
  };

  const handleLanguageChange = (value: string) => {
    const { pathname, origin } = window.location;
    const languageName = SUPPORTED_LANG.find((item) => item.value === value)?.name;

    // Save language preference to cookie
    saveLanguagePreference(value);
    // On a whitelabel site also remember the pick per partner host, so the partner's default
    // language stops redirecting this visitor (see utils/whitelabelLocalization).
    if (config.IS_WHITELABEL_APP) {
      saveWhitelabelLanguageChoice(value);
    }

    // Notify the native app (WebView) so it can persist the choice and localize
    // native UI (e.g. the login modal). No-op outside the app WebView.
    sendWebViewEvent(WEB_VIEW_APP_EVENTS.LANGUAGE_CHANGED, { language: value });

    // Extract current language from the URL path (more reliable than hook state)
    const pathLanguage = extractLanguageFromPath(pathname);
    const currentLang = pathLanguage || userLanguage;

    // Build the URL for the target language
    const urlResult = buildLanguageUrl({
      path: pathname,
      targetLanguage: value,
      currentLanguage: currentLang,
      preserveSearch: true,
    });

    const targetDomain =
      urlResult.needsCrossDomainNavigation && urlResult.targetDomain
        ? urlResult.targetDomain
        : origin;
    const url = new URL(urlResult.url, targetDomain);

    // Remove lang query parameter since language preference is saved in cookie
    url.searchParams.delete("lang");

    // `amber_user_lang` (non-zh) stops the China deployment redirecting a `.com` host to `.cn`
    // (server/ssr.tsx). Leaving `.cn` for another language sets it; picking a Chinese locale
    // clears it, so the last choice wins. The server keeps it as a cookie, so a later switch
    // drops any copy left in the URL rather than carrying a stale value along.
    url.searchParams.delete("amber_user_lang");
    if (isChinaSameDomainLanguage(value)) {
      setCookie({
        cname: "amber_user_lang",
        cvalue: "",
        exdays: -1,
        secure: true,
        domain: getCookieDomain(window.location.hostname),
      });
    } else if (config.IS_CHINA_APP) {
      url.searchParams.set("amber_user_lang", value);
    }

    // Preserve app context across (cross-)domain language switches.
    // The `is-app` request header is only present on the app's initial WebView
    // load, and the `is_app` cookie is scoped to the current root domain, so
    // neither survives a cross-domain switch (e.g. .com -> .cn). Carry the app
    // context via a query param that SSR also honors so app-only UI (e.g. the
    // language selector) keeps rendering on the destination domain.
    if (isApp) {
      url.searchParams.set("is_app", "true");
    }

    // Handle Chinese language special case for UTM parameters
    if (value === "zh") {
      // saveLanguagePreference(value) called above already handles storage

      let utmParamString = "";
      if (window && window?.getCookie) {
        const { getCookie } = window;
        if (getCookie) {
          utmParamString = getCookie("initial_utm_params") || "";
        }
      }

      if (utmParamString && utmParamString.trim() !== "") {
        const utmPairs = utmParamString
          .split("|")
          .map((pair) => pair.trim())
          .filter((pair) => pair && pair.includes("=") && pair.split("=")[1] !== "");

        utmPairs.forEach((pair) => {
          const [key, val] = pair.split("=");
          if (key && val && !url.searchParams.has(key)) {
            url.searchParams.append(key, val);
          }
        });
      }
    }

    logAppEvent({
      eventName: APP_EVENTS.LANGUAGE_CHANGED,
      payload: {
        from_language: currentLang,
        to_language: value,
        to_language_name: languageName,
        source: "homepage_selector",
        is_cross_domain: urlResult.needsCrossDomainNavigation || false,
        navigation_delay_ms: isApp ? 500 : 0,
        current_path: pathname,
      },
    });

    // Perform navigation. Inside the app WebView, delay to ensure:
    // 1. LANGUAGE_CHANGED postMessage is delivered to native
    // 2. Redux state is updated in the app
    // 3. Redux-persist flushes to MMKV storage (can take 100-200ms)
    // This prevents language from reverting to previous value on app restart.
    // In app: Use window.location.replace() to avoid creating history entry.
    // This prevents the back button from taking users to the previous language URL.
    // On web: Use window.location.href to allow normal browser history.
    const navigate = () => {
      if (urlResult.needsCrossDomainNavigation) {
        customDomainSwitch({ url: url.toString() });
      } else if (isApp) {
        window.location.replace(url.toString());
      } else {
        window.location.href = url.toString();
      }
    };

    if (isApp) {
      setTimeout(navigate, 500);
    } else {
      navigate();
    }

    setTimeout(() => {
      handleEventEmitter({
        action: LANGUAGE_SELECTED,
        label: `${LANGUAGE_SELECTED}, ${languageName}`,
      });
    }, 0);
  };
  return { onCLickLanguageSelector, language: userLanguage, handleLanguageChange };
};

export default useLanguageSelector;
